import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ImageIcon,
  Images,
  LayoutTemplate,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

import type { FlowImagePanel as Panel, FlowImageSlot } from "../../../api/flowTypes";
import {
  BRAND_ASSETS_PATH,
  canRegenerate,
  SLOT_KIND_HINT,
  slotKind,
  slotTag,
  templateAllowed,
  type SlotTagTone,
} from "../../../model/flowLabels";
import { CropUploadDialog } from "../host/CropUploadDialog";
import { ProxiedImage } from "../host/ProxiedImage";

function ratioOf(r: string | null): number | null {
  const m = r ? /^(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)$/.exec(r) : null;
  return m ? Number(m[1]) / Number(m[2]) : null;
}

const TAG_TONE: Record<SlotTagTone, string> = {
  ai: "border-primary/40 bg-primary/10 text-primary",
  template: "border-border bg-muted text-muted-foreground",
  own: "border-success/40 bg-success/10 text-success",
  none: "border-warning/50 bg-warning/10 text-warning",
};

function slotName(slot: FlowImageSlot, i: number): string {
  return `${slot.pageLabel} · 图 ${i + 1}`;
}

/**
 * 预览调整页「换图」(1010,`GET flow/images`):全部图片位缩略图,按页分组;每格可
 * 上传 / 从产品图选 / 用模板原图(产品、案例类不给)/ 清空 / AI 重新生成(单张扣费)。
 * ★ 操作都由调用方落到 `PUT flow/images`(只改草稿);重生成是异步任务,结束 api 自动重发预览。
 * ★ `pending`(产品 / 案例类 AI 失败占位)标红并提示重试。
 * ★ 1010 演示机反馈:按页分组、默认折叠(只展开第一页 / 首页);每格标「AI 生成 / 模板原图 / 你的图 / 未配图」;
 *   Logo 位(去品牌资产上传)与二维码位(接入客服后自动生成)不给「AI 重新生成」。
 */
export function FlowImagePanel({
  panel,
  busy,
  regenerating,
  upload,
  onSet,
  onUseTemplate,
  onRegenerate,
}: {
  panel: Panel | null;
  busy?: boolean;
  /** 正在重生成的图片位 token。 */
  regenerating?: string | null;
  /** 裸 base64 + token → 完整 url。 */
  upload: (token: string, base64: string) => Promise<string>;
  /** url="" = 清空。 */
  onSet: (token: string, url: string) => void;
  onUseTemplate: (token: string) => void;
  onRegenerate: (slot: FlowImageSlot, name: string) => void;
}) {
  const [active, setActive] = useState<{ slot: FlowImageSlot; name: string } | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  /** 手动展开 / 收起过的页;没动过的按「第一页展开、其余折叠」。 */
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  if (!panel) return null;

  const pages: { label: string; slots: { slot: FlowImageSlot; i: number }[] }[] = [];
  panel.slots.forEach((slot) => {
    let g = pages.find((p) => p.label === slot.pageLabel);
    if (!g) {
      g = { label: slot.pageLabel, slots: [] };
      pages.push(g);
    }
    g.slots.push({ slot, i: g.slots.length });
  });
  const failed = panel.slots.filter((s) => s.source === "pending").length;

  return (
    <section
      aria-label="换图"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">换图</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          每个图片位可上传、从产品图选,或让 AI 重新画一张(单张 {panel.unitCredits} 积分,当前余额{" "}
          {panel.availableCredits})。换完点「更新预览」。
        </p>
        {failed > 0 && (
          <p role="alert" className="mt-1 text-2xs text-destructive">
            有 {failed} 张图 AI 没生成成功,可以点「AI 重新生成」重试或换成你的图。
          </p>
        )}
      </div>
      {pages.map((g, gi) => {
        const open = toggled[g.label] ?? gi === 0;
        const pending = g.slots.filter(({ slot }) => slot.source === "pending").length;
        return (
          <div key={g.label} role="group" aria-label={`${g.label} 图片位`} className="space-y-2">
            <button
              type="button"
              aria-expanded={open}
              className="flex w-full items-center gap-1 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setToggled((t) => ({ ...t, [g.label]: !open }))}
            >
              {open ? (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              )}
              {g.label}
              <span className="font-normal">({g.slots.length})</span>
              {pending > 0 && (
                <span className="font-normal text-destructive">· {pending} 张待重试</span>
              )}
            </button>
            {open && (
              <ul className="grid gap-2">
                {g.slots.map(({ slot, i }) => {
                  const name = slotName(slot, i);
                  const isRegen = regenerating === slot.token;
                  const kind = slotKind(slot);
                  const tag = slotTag(slot);
                  return (
                    <li
                      key={slot.token}
                      role="group"
                      aria-label={name}
                      data-token={slot.token}
                      data-source={slot.source}
                      className={cn(
                        "flex gap-3 rounded-md border p-2",
                        slot.source === "pending" ? "border-destructive/50" : "border-border/70"
                      )}
                    >
                      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded bg-muted">
                        {slot.url ? (
                          <ProxiedImage
                            value={slot.url}
                            alt={name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="truncate text-xs font-medium text-foreground">
                            {name}
                          </span>
                          <span
                            data-slot-tag={tag.tone}
                            className={cn(
                              "shrink-0 rounded border px-1.5 py-px text-2xs",
                              TAG_TONE[tag.tone]
                            )}
                          >
                            {tag.label}
                          </span>
                        </span>
                        {slot.source === "pending" && (
                          <span className="text-2xs text-destructive">
                            AI 没生成成功,待重试{slot.failedReason ? ` · ${slot.failedReason}` : ""}
                          </span>
                        )}
                        {kind !== "normal" && (
                          <span className="text-2xs text-muted-foreground" data-slot-kind={kind}>
                            {slot.hint || SLOT_KIND_HINT[kind]}
                            {kind === "logo" && (
                              <Link
                                to={BRAND_ASSETS_PATH}
                                className="ml-1 text-primary hover:underline"
                              >
                                去品牌资产
                              </Link>
                            )}
                          </span>
                        )}
                        <div className="mt-auto flex flex-wrap gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={busy}
                            aria-label={`上传:${name}`}
                            onClick={() => setActive({ slot, name })}
                          >
                            <Upload className="h-3 w-3" />
                            上传
                          </Button>
                          <Popover
                            open={picking === slot.token}
                            onOpenChange={(o) => setPicking(o ? slot.token : null)}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                disabled={busy || panel.productImages.length === 0}
                                aria-label={`从产品图选:${name}`}
                                title={
                                  panel.productImages.length === 0 ? "产品库里还没有图" : undefined
                                }
                              >
                                <Images className="h-3 w-3" />
                                产品图
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-2">
                              <ul aria-label="产品图" className="grid grid-cols-3 gap-2">
                                {panel.productImages.map((p) => (
                                  <li key={p.url}>
                                    <button
                                      type="button"
                                      className="flex w-full flex-col items-center gap-1 rounded border border-border/70 p-1 text-2xs hover:border-primary"
                                      aria-label={`用这张:${p.name}`}
                                      onClick={() => {
                                        onSet(slot.token, p.url);
                                        setPicking(null);
                                      }}
                                    >
                                      <span className="grid h-12 w-full place-items-center overflow-hidden rounded bg-muted">
                                        <ProxiedImage
                                          value={p.url}
                                          alt={p.name}
                                          className="h-full w-full object-cover"
                                        />
                                      </span>
                                      <span className="w-full truncate">{p.name}</span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </PopoverContent>
                          </Popover>
                          {canRegenerate(slot) && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              disabled={busy || !!regenerating}
                              aria-label={`AI 重新生成:${name}`}
                              title={`AI 重新画一张,扣 ${panel.unitCredits} 积分;没生成成功不扣`}
                              onClick={() => onRegenerate(slot, name)}
                            >
                              {isRegen ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Sparkles className="h-3 w-3" />
                              )}
                              AI 重新生成
                            </Button>
                          )}
                          {templateAllowed(slot) && slot.source !== "template" && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              disabled={busy}
                              aria-label={`用模板原图:${name}`}
                              onClick={() => onUseTemplate(slot.token)}
                            >
                              <LayoutTemplate className="h-3 w-3" />
                              模板原图
                            </Button>
                          )}
                          {slot.url && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              disabled={busy}
                              aria-label={`清空:${name}`}
                              onClick={() => onSet(slot.token, "")}
                            >
                              <Trash2 className="h-3 w-3" />
                              清空
                            </Button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
      <CropUploadDialog
        open={active !== null}
        onOpenChange={(o) => {
          if (!o) setActive(null);
        }}
        title={active ? `上传 · ${active.name}` : "裁剪后上传"}
        defaultAspect={active ? ratioOf(active.slot.ratio) : null}
        upload={(b64) =>
          active ? upload(active.slot.token, b64) : Promise.reject(new Error("没有选中图片位"))
        }
        onDone={(url) => {
          if (active) onSet(active.slot.token, url);
        }}
      />
    </section>
  );
}
